import { useState, useMemo, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck,
  Lock,
  Check,
  X,
  Tag,
  CreditCard,
  Truck,
  ArrowLeft,
} from "lucide-react";
import { useCart } from "../context/CartContext";
import { useLanguage } from "../context/LanguageContext";
import { createOrder, validateCoupon } from "../lib/api";
import { calcTotals, fmt } from "../lib/pricing";
import {
  VisaLogo,
  MastercardLogo,
  AmexLogo,
  PaypalLogo,
  ApplePayLogo,
} from "../components/PaymentLogos";
import { ProductImg } from "../components/ProductImg";

export default function Checkout() {
  const { items: cartItems, subtotal: cartSubtotal, clear, coupon, setCoupon, clearCoupon } = useCart();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  // Buy Now flow — single item override
  const buyNowItem = location.state?.buyNowItem;
  const items = buyNowItem ? [buyNowItem] : cartItems;
  const subtotal = buyNowItem
    ? buyNowItem.price * buyNowItem.quantity
    : cartSubtotal;

  const [form, setForm] = useState({
    email: "",
    full_name: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    postal_code: "",
    country: "Mexico",
  });
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [card, setCard] = useState({ number: "", expiry: "", cvc: "", name: "" });
  const [loading, setLoading] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [applyingCoupon, setApplyingCoupon] = useState(false);

  const totals = useMemo(() => calcTotals(subtotal, coupon), [subtotal, coupon]);

  // If cart empty AND not buy-now, redirect gently
  useEffect(() => {
    if (!buyNowItem && cartItems.length === 0) {
      // stay but show empty state
    }
  }, [buyNowItem, cartItems.length]);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onCardChange = (e) => {
    let { name, value } = e.target;
    if (name === "number") {
      value = value.replace(/\D/g, "").slice(0, 19);
      value = value.replace(/(.{4})/g, "$1 ").trim();
    } else if (name === "expiry") {
      value = value.replace(/\D/g, "").slice(0, 4);
      if (value.length >= 3) value = value.slice(0, 2) + " / " + value.slice(2);
    } else if (name === "cvc") {
      value = value.replace(/\D/g, "").slice(0, 4);
    }
    setCard((c) => ({ ...c, [name]: value }));
  };

  const handleApplyCoupon = async () => {
    if (!couponInput.trim()) return;
    setApplyingCoupon(true);
    try {
      const result = await validateCoupon(couponInput.trim(), subtotal);
      if (result.valid) {
        setCoupon(result);
        setCouponInput("");
        toast.success(result.message);
      } else {
        toast.error(t.cart.couponInvalid);
      }
    } catch {
      toast.error(t.cart.couponInvalid);
    } finally {
      setApplyingCoupon(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (items.length === 0) {
      toast.error(t.cart.empty);
      return;
    }
    if (paymentMethod === "card") {
      if (!card.number.trim() || !card.expiry.trim() || !card.cvc.trim() || !card.name.trim()) {
        toast.error(t.checkoutMisc.cardMissing);
        return;
      }
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        payment_method: paymentMethod,
        coupon_code: coupon?.valid ? coupon.code : null,
        items: items.map((i) => ({
          product_id: i.product_id,
          slug: i.slug,
          name: i.name,
          price: i.price,
          quantity: i.quantity,
          size: i.size,
          color: i.color,
          image: i.image,
        })),
      };
      const order = await createOrder(payload);
      if (!buyNowItem) clear();
      else clearCoupon();
      navigate(`/order-success?n=${encodeURIComponent(order.order_number)}`, {
        state: { order, shippingForm: form },
      });
    } catch (err) {
      toast.error(t.checkoutMisc.orderFailed);
    } finally {
      setLoading(false);
    }
  };

  const empty = items.length === 0;

  return (
    <main data-testid="checkout-page" className="bg-black min-h-screen pt-24 md:pt-28 pb-20">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10">
        <Link
          to="/shop"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/60 hover:text-yellow-400 transition-colors mt-8 mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> {t.checkout.backToShop}
        </Link>

        <div className="border-b border-white/10 pb-8 mb-10">
          <p className="overline mb-2 flex items-center gap-2">
            <Lock className="w-3 h-3" /> {t.checkout.secureNote}
          </p>
          <h1 className="h-display text-white text-5xl md:text-7xl">{t.checkout.title}</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Form */}
          <form
            onSubmit={handleSubmit}
            data-testid="checkout-form"
            className="lg:col-span-7 space-y-10"
          >
            {/* Step 1 — Contact */}
            <fieldset>
              <legend className="overline mb-5">01 · {t.checkout.step1}</legend>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  required
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  placeholder={t.checkout.email}
                  data-testid="checkout-email"
                  className="w-full bg-transparent border border-white/20 px-4 py-4 text-sm focus:outline-none focus:border-yellow-400 placeholder-white/40"
                />
                <input
                  required
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  placeholder={t.checkout.phone}
                  data-testid="checkout-phone"
                  className="w-full bg-transparent border border-white/20 px-4 py-4 text-sm focus:outline-none focus:border-yellow-400 placeholder-white/40"
                />
              </div>
            </fieldset>

            {/* Step 2 — Shipping */}
            <fieldset className="space-y-3">
              <legend className="overline mb-5 flex items-center gap-2">
                <Truck className="w-3 h-3" /> 02 · {t.checkout.step2}
              </legend>
              <input
                required
                name="full_name"
                value={form.full_name}
                onChange={onChange}
                placeholder={t.checkout.fullName}
                data-testid="checkout-fullname"
                className="w-full bg-transparent border border-white/20 px-4 py-4 text-sm focus:outline-none focus:border-yellow-400 placeholder-white/40"
              />
              <input
                required
                name="address"
                value={form.address}
                onChange={onChange}
                placeholder={t.checkout.address}
                data-testid="checkout-address"
                className="w-full bg-transparent border border-white/20 px-4 py-4 text-sm focus:outline-none focus:border-yellow-400 placeholder-white/40"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  required
                  name="city"
                  value={form.city}
                  onChange={onChange}
                  placeholder={t.checkout.city}
                  data-testid="checkout-city"
                  className="w-full bg-transparent border border-white/20 px-4 py-4 text-sm focus:outline-none focus:border-yellow-400 placeholder-white/40"
                />
                <input
                  required
                  name="state"
                  value={form.state}
                  onChange={onChange}
                  placeholder={t.checkout.state}
                  data-testid="checkout-state"
                  className="w-full bg-transparent border border-white/20 px-4 py-4 text-sm focus:outline-none focus:border-yellow-400 placeholder-white/40"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  required
                  name="postal_code"
                  value={form.postal_code}
                  onChange={onChange}
                  placeholder={t.checkout.postal}
                  data-testid="checkout-postal"
                  className="w-full bg-transparent border border-white/20 px-4 py-4 text-sm focus:outline-none focus:border-yellow-400 placeholder-white/40"
                />
                <input
                  required
                  name="country"
                  value={form.country}
                  onChange={onChange}
                  placeholder={t.checkout.country}
                  data-testid="checkout-country"
                  className="w-full bg-transparent border border-white/20 px-4 py-4 text-sm focus:outline-none focus:border-yellow-400 placeholder-white/40"
                />
              </div>
            </fieldset>

            {/* Step 3 — Payment */}
            <fieldset>
              <legend className="overline mb-5 flex items-center gap-2">
                <CreditCard className="w-3 h-3" /> 03 · {t.checkout.step3}
              </legend>

              <div
                className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-5"
                data-testid="payment-methods"
              >
                {[
                  {
                    id: "card",
                    label: t.checkout.card,
                    logos: (
                      <div className="flex gap-1">
                        <VisaLogo className="h-4" />
                        <MastercardLogo className="h-4" />
                        <AmexLogo className="h-4" />
                      </div>
                    ),
                  },
                  {
                    id: "paypal",
                    label: t.checkout.paypal,
                    logos: <PaypalLogo className="h-4" />,
                  },
                  {
                    id: "apple_pay",
                    label: t.checkout.applePay,
                    logos: <ApplePayLogo className="h-4" />,
                  },
                ].map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setPaymentMethod(m.id)}
                    data-testid={`payment-${m.id}`}
                    className={`flex flex-col items-start gap-2 border p-4 text-left transition-colors ${
                      paymentMethod === m.id
                        ? "border-yellow-400 bg-yellow-400/5"
                        : "border-white/15 hover:border-white/40"
                    }`}
                  >
                    <div className="flex items-center justify-between w-full">
                      <span className="text-[11px] uppercase tracking-[0.2em] font-bold">
                        {m.label}
                      </span>
                      <span
                        className={`w-4 h-4 border ${
                          paymentMethod === m.id
                            ? "bg-yellow-400 border-yellow-400"
                            : "border-white/40"
                        } flex items-center justify-center`}
                      >
                        {paymentMethod === m.id && (
                          <Check className="w-3 h-3 text-black" strokeWidth={3} />
                        )}
                      </span>
                    </div>
                    <div>{m.logos}</div>
                  </button>
                ))}
              </div>

              {paymentMethod === "card" && (
                <div className="border border-white/15 p-5 space-y-3" data-testid="card-form">
                  <input
                    name="number"
                    value={card.number}
                    onChange={onCardChange}
                    placeholder={t.checkout.cardNumber + " · 4242 4242 4242 4242"}
                    data-testid="card-number"
                    className="w-full bg-transparent border border-white/20 px-4 py-4 text-sm focus:outline-none focus:border-yellow-400 placeholder-white/40 font-mono"
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      name="expiry"
                      value={card.expiry}
                      onChange={onCardChange}
                      placeholder={t.checkout.expiry}
                      data-testid="card-expiry"
                      className="w-full bg-transparent border border-white/20 px-4 py-4 text-sm focus:outline-none focus:border-yellow-400 placeholder-white/40 font-mono"
                    />
                    <input
                      name="cvc"
                      value={card.cvc}
                      onChange={onCardChange}
                      placeholder={t.checkout.cvc}
                      data-testid="card-cvc"
                      className="w-full bg-transparent border border-white/20 px-4 py-4 text-sm focus:outline-none focus:border-yellow-400 placeholder-white/40 font-mono"
                    />
                  </div>
                  <input
                    name="name"
                    value={card.name}
                    onChange={onCardChange}
                    placeholder={t.checkout.cardName}
                    data-testid="card-name"
                    className="w-full bg-transparent border border-white/20 px-4 py-4 text-sm focus:outline-none focus:border-yellow-400 placeholder-white/40"
                  />
                </div>
              )}

              {paymentMethod === "paypal" && (
                <div
                  data-testid="paypal-notice"
                  className="border border-white/15 p-6 flex items-center gap-4 text-sm text-white/60"
                >
                  <PaypalLogo className="h-8 flex-shrink-0" />
                  <p>{t.checkout.redirectPaypal}</p>
                </div>
              )}

              {paymentMethod === "apple_pay" && (
                <div
                  data-testid="applepay-notice"
                  className="border border-white/15 p-6 flex items-center gap-4 text-sm text-white/60"
                >
                  <ApplePayLogo className="h-8 flex-shrink-0" />
                  <p>{t.checkout.redirectApple}</p>
                </div>
              )}

              <div className="mt-4 flex items-center gap-4 text-[10px] uppercase tracking-[0.25em] text-white/40">
                <span className="flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3 text-yellow-400" /> SSL 256-bit
                </span>
                <span className="flex items-center gap-1">
                  <Lock className="w-3 h-3 text-yellow-400" /> PCI DSS
                </span>
                <span>· {t.checkoutMisc.noCardStored}</span>
              </div>
            </fieldset>

            <button
              type="submit"
              disabled={loading || empty}
              data-testid="place-order-button"
              className="w-full bg-yellow-400 text-black py-5 font-bold uppercase tracking-[0.25em] text-xs hover:bg-yellow-300 transition-colors disabled:opacity-50"
            >
              {loading
                ? t.checkout.placing
                : `${t.checkout.placeOrder} — ${fmt(totals.total)}`}
            </button>
          </form>

          {/* Summary */}
          <aside className="lg:col-span-5 lg:sticky lg:top-28 lg:self-start">
            <div className="border border-white/10 p-6 md:p-8 bg-zinc-950">
              <h3 className="overline mb-6">{t.checkout.orderSummary}</h3>
              {empty ? (
                <p className="text-white/60 text-sm">
                  {t.cart.empty}.{" "}
                  <Link to="/shop" className="text-yellow-400 underline">
                    {t.cart.emptyCta}
                  </Link>
                </p>
              ) : (
                <>
                  <ul className="space-y-5 max-h-[320px] overflow-y-auto pr-2">
                    {items.map((i, idx) => (
                      <li
                        key={idx}
                        className="flex gap-3"
                        data-testid={`summary-item-${idx}`}
                      >
                        <div className="w-16 h-20 bg-zinc-900 overflow-hidden flex-shrink-0 relative">
                          {i.image && (
                            <ProductImg
                              src={i.image}
                              alt=""
                              className="w-full h-full object-cover"
                              small
                            />
                          )}
                          <span className="absolute -top-1 -right-1 bg-yellow-400 text-black text-[10px] font-black w-5 h-5 flex items-center justify-center">
                            {i.quantity}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold uppercase truncate">
                            {i.name}
                          </p>
                          <p className="text-[11px] text-white/50 uppercase tracking-widest mt-1">
                            {i.size && <>{i.size} · </>}
                            {i.color && <>{i.color}</>}
                          </p>
                        </div>
                        <p className="text-sm font-bold">
                          ${(i.price * i.quantity).toFixed(2)}
                        </p>
                      </li>
                    ))}
                  </ul>

                  {/* Coupon (if not already applied) */}
                  <div className="mt-6 border-t border-white/10 pt-5">
                    {coupon?.valid ? (
                      <div
                        data-testid="checkout-coupon-applied"
                        className="flex items-center justify-between border border-yellow-400/50 bg-yellow-400/10 px-3 py-2.5 text-sm"
                      >
                        <span className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-yellow-400" />
                          <span className="font-bold text-yellow-400">
                            {coupon.code}
                          </span>
                          <span className="text-white/60 text-xs">
                            · {coupon.message}
                          </span>
                        </span>
                        <button
                          onClick={clearCoupon}
                          type="button"
                          data-testid="checkout-coupon-remove"
                          className="text-white/50 hover:text-yellow-400"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-0">
                        <div className="flex-1 flex items-center border border-white/20 focus-within:border-yellow-400 transition-colors">
                          <Tag className="w-4 h-4 mx-3 text-white/40" />
                          <input
                            type="text"
                            value={couponInput}
                            onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                            placeholder={t.cart.couponPlaceholder}
                            data-testid="checkout-coupon-input"
                            className="flex-1 bg-transparent py-3 text-sm focus:outline-none placeholder-white/40"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={handleApplyCoupon}
                          disabled={applyingCoupon || !couponInput.trim()}
                          data-testid="checkout-coupon-apply"
                          className="bg-white text-black px-5 font-bold uppercase tracking-widest text-xs hover:bg-yellow-400 transition-colors disabled:opacity-40"
                        >
                          {applyingCoupon ? "..." : t.cart.apply}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Totals */}
                  <div className="mt-6 pt-6 border-t border-white/10 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-white/60">{t.cart.subtotal}</span>
                      <span data-testid="summary-subtotal">
                        {fmt(totals.subtotal)}
                      </span>
                    </div>
                    {totals.discount > 0 && (
                      <div
                        data-testid="summary-discount"
                        className="flex justify-between text-yellow-400"
                      >
                        <span>{t.cart.discount}</span>
                        <span>-{fmt(totals.discount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-white/60">{t.cart.shipping}</span>
                      <span data-testid="summary-shipping">
                        {totals.shipping === 0 ? (
                          <span className="text-yellow-400 font-bold uppercase text-xs tracking-widest">
                            {t.cart.free}
                          </span>
                        ) : (
                          fmt(totals.shipping)
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/60">{t.cart.tax}</span>
                      <span data-testid="summary-tax">{fmt(totals.tax)}</span>
                    </div>
                    <div className="flex justify-between font-display text-xl uppercase pt-3 border-t border-white/10 mt-3">
                      <span>{t.cart.total}</span>
                      <span data-testid="checkout-total">{fmt(totals.total)}</span>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Trust badges */}
            <div className="mt-4 grid grid-cols-2 gap-3 text-[10px] uppercase tracking-[0.2em] text-white/50">
              <div className="border border-white/10 px-3 py-3 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-yellow-400" />
                {t.checkoutMisc.trustSecure}
              </div>
              <div className="border border-white/10 px-3 py-3 flex items-center gap-2">
                <Truck className="w-4 h-4 text-yellow-400" />
                {t.checkoutMisc.trustShip}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}
